import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { useTranslation } from "react-i18next";
import SEOHead from "../../components/shared/SEOHead.jsx";
import JsonLd from "../../components/shared/JsonLd.jsx";
import ResponsiveImage from "../../components/shared/ResponsiveImage.jsx";
import imageManifest from "../../data/imageManifest.json";
import Breadcrumbs from "../../components/shared/Breadcrumbs.jsx";
import ShareLinks from "../../components/shared/ShareLinks.jsx";
import ProgressBar from "../../components/spenden/ProgressBar.jsx";
import StepIndicator from "../../components/spenden/StepIndicator.jsx";
import DonationForm from "../../components/spenden/DonationForm.jsx";
import { events, spendenProjects, getSpendenProjectBySlug, findByLangAndId, formatDate } from "../../utils/content.js";
import { API_BASE_URL } from "../../utils/api.js";
import { buildBreadcrumbJsonLd, buildArticleJsonLd } from "../../utils/jsonld.js";
import NotFound from "../NotFound.jsx";

const SPENDEN_PATH = { de: "spenden", en: "donate" };

/** Live raised/donors per §7.3 — frontmatter values are the SEO/first-paint fallback only. */
function useLiveProgress(slug, fallback) {
  const [progress, setProgress] = useState(fallback);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/donations/progress/${slug}`)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then(setProgress)
      .catch(() => {}); // keep the frontmatter fallback on any failure
  }, [slug]);

  return progress;
}

export default function DonationDetail({ lang }) {
  const { slug } = useParams();
  const { t } = useTranslation();
  const project = getSpendenProjectBySlug(lang, slug);
  const progress = useLiveProgress(slug, project ? { raised: project.frontmatter.raised, donors: project.frontmatter.donors } : null);
  const heroImageLoaded = Boolean(project && imageManifest[project.frontmatter.image]);

  if (!project) return <NotFound lang={lang} />;

  const { frontmatter, content } = project;
  const otherLang = lang === "de" ? "en" : "de";
  // Sibling found by `id`, not by assuming the same slug — donation
  // projects happen to share slugs across languages today, but nothing
  // requires it.
  const sibling = findByLangAndId(spendenProjects, otherLang, frontmatter.id);
  const path = `/${SPENDEN_PATH[lang]}/${slug}`;
  const altPath = sibling ? `/${SPENDEN_PATH[otherLang]}/${sibling.frontmatter.slug}` : path;
  const hasTranslation = Boolean(sibling);
  // Resolved from the language-neutral event `id`, never from a slug
  // written into the markdown body — the donation project's own slug and
  // the event's slug differ per language, and hardcoding one into the
  // other language's copy is exactly what produced a live 404 (§ T-03).
  const relatedEvent = findByLangAndId(events, lang, frontmatter.relatedEvent);
  const pct = Math.min(100, Math.round((progress.raised / frontmatter.goal) * 100));
  const breadcrumbItems = [
    { name: t("nav.home"), path: "/" },
    { name: t("nav.spenden"), path: `/${SPENDEN_PATH[lang]}` },
    { name: frontmatter.title, path },
  ];

  return (
    <>
      <SEOHead
        lang={lang}
        path={path}
        altPath={altPath}
        hasTranslation={hasTranslation}
        title={frontmatter.metaTitle || frontmatter.title}
        description={frontmatter.metaDescription}
        image={frontmatter.image}
        imageAlt={frontmatter.imageAlt}
        type="article"
        publishedTime={frontmatter.date}
        modifiedTime={frontmatter.updated || frontmatter.date}
      />
      <JsonLd data={buildBreadcrumbJsonLd(breadcrumbItems, lang)} />
      <JsonLd
        data={buildArticleJsonLd({
          title: frontmatter.title,
          url: `https://talesandlegends.org/${lang}${path}`,
          lang,
          description: frontmatter.metaDescription,
          image: frontmatter.image,
          datePublished: frontmatter.date,
          dateModified: frontmatter.updated || frontmatter.date,
        })}
      />

      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div className="tl-container" style={{ display: "flex", flexDirection: "column", gap: "40px", paddingTop: "32px", paddingBottom: "96px" }}>
          <Breadcrumbs items={breadcrumbItems} lang={lang} />

          <div style={{ position: "relative", borderRadius: "14px", overflow: "hidden" }}>
            <ResponsiveImage
              src={frontmatter.image}
              alt={frontmatter.imageAlt || frontmatter.title}
              priority
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
            />
            {heroImageLoaded && (
              <div
                style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(11,9,8,0.55), rgba(11,9,8,0.75))" }}
              />
            )}
            <div style={{ position: "relative", display: "flex", flexDirection: "column", gap: "22px", padding: "56px 40px" }}>
              <h1 className="tl-hero-h1" style={{ fontFamily: "var(--font-serif)", fontSize: "44px", lineHeight: 1.1, color: "var(--color-cream)", maxWidth: "24ch", margin: 0 }}>
                {frontmatter.title}
              </h1>
              {frontmatter.date && (
                <span style={{ fontSize: "13px", color: "var(--color-cream-faint)" }}>
                  {t("common.publishedOn", { date: formatDate(frontmatter.date, lang) }) +
                    (frontmatter.updated && frontmatter.updated !== frontmatter.date
                      ? ` · ${t("common.updatedOn", { date: formatDate(frontmatter.updated, lang) })}`
                      : "")}
                </span>
              )}
              <ShareLinks shortUrl={frontmatter.shortUrl} title={frontmatter.title} />
            </div>
          </div>

          <div className="tl-grid-2" style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: "32px", alignItems: "start" }}>
            <article style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
              <div className="tl-card" style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <ProgressBar raised={progress.raised} goal={frontmatter.goal} dark />
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "14px", color: "var(--color-cream-soft)" }}>
                  <span>
                    {`${progress.raised.toLocaleString(lang)} € ${t("spenden.raisedOf")} ${frontmatter.goal.toLocaleString(lang)} €`}
                  </span>
                  <span>
                    {`${pct} % · ${progress.donors} ${t("spenden.supportersLabel")}`}
                  </span>
                </div>
              </div>

              {frontmatter.steps && (
                <div className="tl-card" style={{ padding: "16px" }}>
                  <StepIndicator steps={frontmatter.steps} raised={progress.raised} />
                </div>
              )}

              <div style={{ fontSize: "16px", lineHeight: 1.75, color: "var(--color-cream-soft)" }}>
                <ReactMarkdown>{content}</ReactMarkdown>
              </div>

              {relatedEvent && (
                <Link
                  to={`/${lang}/events/${relatedEvent.frontmatter.slug}`}
                  className="tl-pill tl-pill-outline"
                  style={{ alignSelf: "flex-start" }}
                >
                  {t("spenden.relatedEventLink")}
                </Link>
              )}
            </article>

            <div style={{ display: "flex", flexDirection: "column", gap: "20px", position: "sticky", top: "96px" }}>
              <div className="tl-card-light tl-form-card-tight">
                {frontmatter.status === "completed" ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                    <span style={{ fontFamily: "var(--font-serif)", fontSize: "22px", color: "var(--color-ink)" }}>
                      {t("spenden.completedThanksTitle")}
                    </span>
                    <p style={{ fontSize: "15px", lineHeight: 1.7, color: "rgba(10,9,7,0.72)", margin: 0 }}>
                      {t("spenden.completedThanksBody")}
                    </p>
                  </div>
                ) : (
                  <DonationForm lang={lang} projectSlug={frontmatter.slug} projectTitle={frontmatter.title} />
                )}
              </div>

              {frontmatter.status === "completed" && frontmatter.documents?.length > 0 && (
                <div className="tl-card-light" style={{ padding: "28px", display: "flex", flexDirection: "column", gap: "14px" }}>
                  <span style={{ fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: "17px", color: "var(--color-ink)" }}>
                    {t("spenden.documentsTitle")}
                  </span>
                  <ul style={{ display: "flex", flexDirection: "column", gap: "12px", margin: 0, padding: 0, listStyle: "none" }}>
                    {frontmatter.documents.map((doc) => (
                      <li key={doc.file}>
                        <a href={doc.file} target="_blank" rel="noopener noreferrer" style={{ fontSize: "15px" }}>
                          {doc.title}
                        </a>
                        {doc.description && (
                          <p style={{ fontSize: "16px", lineHeight: 1.6, color: "rgba(10,9,7,0.65)", margin: "4px 0 0" }}>
                            {doc.description}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
